/* global document */
import * as React from "react"
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react"
import { createPortal } from "react-dom"
import {
  Alignment,
  Marker as MapLibreMarker,
  MarkerOptions,
  PointLike,
  Popup,
} from "maplibre-gl"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"

export type MarkerDragEvent = {
  type: string
  target: MapLibreMarker
  lngLat: { lng: number; lat: number }
}

export type MarkerEvent<T = Event> = {
  type: string
  target: MapLibreMarker
  originalEvent: T
}

export type MarkerProps = Omit<MarkerOptions, "element"> & {
  /** Longitude of the anchor location */
  longitude: number
  /** Latitude of the anchor location */
  latitude: number

  popup?: Popup | null

  /** CSS style override, applied to the control's container */
  style?: React.CSSProperties
  className?: string
  onClick?: (e: MarkerEvent<MouseEvent>) => void
  onDragStart?: (e: MarkerDragEvent) => void
  onDrag?: (e: MarkerDragEvent) => void
  onDragEnd?: (e: MarkerDragEvent) => void
  children?: React.ReactNode
}

function applyReactStyle(
  element: HTMLElement,
  style: React.CSSProperties | undefined,
) {
  if (!element || !style) return
  for (const key in style) {
    if (Object.prototype.hasOwnProperty.call(style, key)) {
      // @ts-ignore
      element.style[key] = style[key]
    }
  }
}

function arePointsEqual(
  a: PointLike | undefined,
  b: PointLike | undefined,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    return a[0] === b[0] && a[1] === b[1]
  }
  if (!Array.isArray(a) && !Array.isArray(b)) {
    return a.x === b.x && a.y === b.y
  }
  return false
}

function compareClassNames(
  oldClassName: string | undefined,
  newClassName: string | undefined,
): string[] | null {
  const oldClasses = new Set((oldClassName || "").split(" ").filter(Boolean))
  const newClasses = new Set((newClassName || "").split(" ").filter(Boolean))

  const diff: string[] = []

  // Classes that were added
  for (const c of newClasses) {
    if (!oldClasses.has(c)) {
      diff.push(c)
    }
  }
  // Classes that were removed
  for (const c of oldClasses) {
    if (!newClasses.has(c)) {
      diff.push(c)
    }
  }

  return diff.length > 0 ? diff : null
}

const Marker = memo(
  forwardRef((props: MarkerProps, ref: React.Ref<MapLibreMarker>) => {
    const mapManager = useMapContext()
    const map = mapManager?.getMapInstance()
    const thisRef = useRef({ props })

    const marker: MapLibreMarker | null = useMemo(() => {
      if (!map) return null

      let hasChildren = false
      React.Children.forEach(props.children, (el) => {
        if (el) {
          hasChildren = true
        }
      })
      const options: MarkerOptions = {
        ...props,
        element: hasChildren ? document.createElement("div") : undefined,
      }

      const mk = new MapLibreMarker(options)
      mk.setLngLat([props.longitude, props.latitude])

      mk.getElement().addEventListener("click", (e: MouseEvent) => {
        thisRef.current.props.onClick?.({
          type: "click",
          target: mk,
          originalEvent: e,
        })
      })

      mk.on("dragstart", (e) => {
        const evt = e as unknown as MarkerDragEvent
        evt.lngLat = mk.getLngLat()
        thisRef.current.props.onDragStart?.(evt)
      })
      mk.on("drag", (e) => {
        const evt = e as unknown as MarkerDragEvent
        evt.lngLat = mk.getLngLat()
        thisRef.current.props.onDrag?.(evt)
      })
      mk.on("dragend", (e) => {
        const evt = e as unknown as MarkerDragEvent
        evt.lngLat = mk.getLngLat()
        thisRef.current.props.onDragEnd?.(evt)
      })

      return mk
    }, [map])

    useEffect(() => {
      if (marker && map) {
        marker.addTo(map)

        return () => {
          marker.remove()
        }
      }
      return undefined
    }, [marker, map])

    const {
      longitude,
      latitude,
      offset,
      style,
      draggable = false,
      popup = null,
      rotation = 0,
      rotationAlignment = "auto",
      pitchAlignment = "auto",
      className,
    } = props

    useEffect(() => {
      if (marker) {
        applyReactStyle(marker.getElement(), style)
      }
    }, [marker, style])

    useImperativeHandle(ref, () => marker!, [marker])

    if (!marker) return null

    const oldProps = thisRef.current.props
    if (
      marker.getLngLat().lng !== longitude ||
      marker.getLngLat().lat !== latitude
    ) {
      marker.setLngLat([longitude, latitude])
    }
    if (offset && !arePointsEqual(marker.getOffset(), offset)) {
      marker.setOffset(offset)
    }
    if (marker.isDraggable() !== draggable) {
      marker.setDraggable(draggable)
    }
    if (marker.getRotation() !== rotation) {
      marker.setRotation(rotation)
    }
    if (marker.getRotationAlignment() !== rotationAlignment) {
      marker.setRotationAlignment(rotationAlignment as Alignment)
    }
    if (marker.getPitchAlignment() !== pitchAlignment) {
      marker.setPitchAlignment(pitchAlignment as Alignment)
    }
    if (marker.getPopup() !== popup) {
      marker.setPopup(popup)
    }
    const classNameDiff = compareClassNames(oldProps.className, className)
    if (classNameDiff) {
      for (const c of classNameDiff) {
        marker.toggleClassName(c)
      }
    }

    thisRef.current.props = props
    return createPortal(props.children, marker.getElement())
  }),
)

export default Marker
